import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { CMPS_DIALOG_DATA, CmpsDialogRef } from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
    CmpsForm,
    CmpsFormBuilder,
    CmpsFormField,
    CmpsFormFields,
} from '../../common/experimental.form';
import { TranslationKeyMap } from '../auth.i18n';
import { TokenRequest } from '../auth.model';
import { AuthService } from '../auth.service';

import { ValidateDialogData } from './validate-dialog-data.auth.model';

interface ValidateFormFields extends CmpsFormFields {
    USERNAME: CmpsFormField;
    PASSWORD: CmpsFormField;
}

const ValidateFormFieldsDef: ValidateFormFields = {
    USERNAME: {
        id: 'username',
        name: 'username',
        label: TranslationKeyMap._72_USERNAME,
        placeholder: TranslationKeyMap._72_USERNAME,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
        },
    },
    PASSWORD: {
        id: 'password',
        name: 'password',
        label: TranslationKeyMap._55_PASSWORD,
        placeholder: TranslationKeyMap._55_PASSWORD,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
        },
    },
};

@Component({
    selector: 'cmps-auth-validate',
    template: ` <div class="cmps-auth-validate--header">
            <div
                class="signoff-text"
                role="heading"
                aria-level="1"
            >
                <span>{{ _options?.intentMessage }}</span>
            </div>
            <div>
                <button
                    type="submit"
                    class="cmps-outline-button--sm"
                    cmpsOutlineButton
                    form="cmpsAuthValidateForm"
                    [disabled]="pending"
                >
                    <span cmpsOutlineButtonLabel>{{ _options?.intent }}</span>
                </button>
            </div>
            <button
                class="cmps-icon-button--no-bg cmps-icon-button--no-bg--white cmps-auth-validate--close"
                cmpsIconButton
                (click)="_dialogRef?.close()"
            >
                <cmps-icon svgIcon="close"></cmps-icon>
            </button>
        </div>
        <div class="container cmps-auth-validate--body">
            <div class="row">
                <div class="col-12 m-auto py-3">
                    <form
                        id="cmpsAuthValidateForm"
                        [formGroup]="form.form"
                        (ngSubmit)="onSubmit()"
                    >
                        <div class="row mt-3">
                            <div class="col-4 my-auto text-right">
                                <label
                                    class="mb-0 font-weight-bold"
                                    [for]="form.fields.USERNAME.id"
                                    noMarkAsRequired
                                >
                                    {{ form.fields.USERNAME.label | translate }}
                                </label>
                            </div>
                            <div class="col-6 pl-0">
                                <input
                                    type="text"
                                    class="form-control text-center cmps-auth-validate--input"
                                    [name]="form.fields.USERNAME.name"
                                    [placeholder]="
                                        form.fields.USERNAME.placeholder
                                            | translate
                                    "
                                    [formControlName]="
                                        form.fields.USERNAME.name
                                    "
                                />
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div class="offset-4 col-6 pl-0">
                                <div
                                    class="alert alert-warning mt-1 mb-0"
                                    role="alert"
                                    *ngIf="form.errors['USERNAME']"
                                >
                                    {{ form.errors['USERNAME'] }}
                                </div>
                            </div>
                        </div>
                        <div class="row mt-2">
                            <div class="col-4 my-auto text-right">
                                <label
                                    class="mb-0 font-weight-bold"
                                    [for]="form.fields.PASSWORD.id"
                                    noMarkAsRequired
                                >
                                    {{ form.fields.PASSWORD.label | translate }}
                                </label>
                            </div>
                            <div class="col-6 pl-0">
                                <input
                                    type="password"
                                    class="form-control text-center cmps-auth-validate--input"
                                    autocomplete="off"
                                    [name]="form.fields.PASSWORD.name"
                                    [placeholder]="
                                        form.fields.PASSWORD.placeholder
                                            | translate
                                    "
                                    [formControlName]="
                                        form.fields.PASSWORD.name
                                    "
                                />
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="offset-4 col-6 pl-0">
                                <div
                                    class="alert alert-warning mt-1 mb-0"
                                    role="alert"
                                    *ngIf="form.errors['PASSWORD']"
                                >
                                    {{ form.errors['PASSWORD'] }}
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-12">
                                <div
                                    class="alert alert-warning mb-0"
                                    role="alert"
                                    *ngIf="failure"
                                >
                                    {{ serverError }}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>`,
    host: {
        class: 'cmps-auth-validate',
    },
})
export class ValidateAuthComponent implements OnInit, OnDestroy {
    i18n = TranslationKeyMap;
    form: CmpsForm<ValidateFormFields>;
    private _sub: Subscription;
    pending: boolean;
    failure: boolean;
    serverError: string;

    constructor(
        private readonly _translateService: TranslateService,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _authService: AuthService,
        /** @private */ readonly _dialogRef: CmpsDialogRef<
            ValidateAuthComponent,
            boolean
        >,
        /** @private */ @Inject(CMPS_DIALOG_DATA)
        readonly _options: ValidateDialogData,
    ) {}

    ngOnInit(): void {
        this.form = this._formBuilder.create<ValidateFormFields>(
            ValidateFormFieldsDef,
        );
        this.form.form.reset(
            {
                username: { value: this._options.username, disabled: true },
            },
            { emitEvent: false },
        );
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._sub && this._sub.unsubscribe();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.form && this.form.destroy();
    }

    onSubmit(): void {
        this.form.checkWarningsErrors();
        if (!this.form.form.valid) {
            return;
        }

        this.form.form.controls[this.form.fields.PASSWORD.name].disable({
            emitEvent: false,
        });
        this.pending = true;
        this.failure = false;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._sub && this._sub.unsubscribe();
        const req: TokenRequest = {
            ...this.form.form.value,
            languageId: parseInt(this._translateService.currentLang),
        };
        this._sub = this._authService
            .validateCredentials(req)
            .pipe(
                finalize(() => {
                    this.pending = false;
                    this.form.form.controls[
                        this.form.fields.PASSWORD.name
                    ].enable({ emitEvent: false });
                }),
            )
            .subscribe({
                complete: () => {
                    this._dialogRef.close(true);
                },
                error: (err) => {
                    this.failure = true;
                    this.serverError =
                        err.message ||
                        this._translateService.instant(
                            TranslationKeyMap._704_UNKNOWN_ERROR,
                        );
                },
            });
    }
}
